/**
 * Toast Container Component
 * 
 * Container for managing multiple toast notifications
 * Supports different positions and stacking
 */

/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import Toast, { ToastProps } from './Toast';
import { clsx } from 'clsx';

export interface ToastContextType {
  showToast: (toast: Omit<ToastProps, 'id' | 'onClose'>) => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
  showWarning: (title: string, message?: string) => void;
  showInfo: (title: string, message?: string) => void;
  closeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

interface ToastProviderProps {
  children: ReactNode;
  position?: 'top-left' | 'top-right' | 'top-center' | 'bottom-left' | 'bottom-right' | 'bottom-center';
  maxToasts?: number;
}

export function ToastProvider({
  children,
  position = 'top-right',
  maxToasts = 5,
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<(ToastProps & { id: string })[]>([]);

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const closeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((toast: Omit<ToastProps, 'id' | 'onClose'>) => {
    const id = generateId();
    const newToast = { ...toast, id, onClose: closeToast };
    
    setToasts((prev) => {
      const updated = [...prev, newToast];
      // Limit number of toasts
      if (updated.length > maxToasts) {
        return updated.slice(updated.length - maxToasts);
      }
      return updated;
    });
  }, [closeToast, maxToasts]);

  const showSuccess = useCallback((title: string, message?: string) => {
    showToast({ variant: 'success', title, message });
  }, [showToast]);

  const showError = useCallback((title: string, message?: string) => {
    showToast({ variant: 'error', title, message });
  }, [showToast]);

  const showWarning = useCallback((title: string, message?: string) => {
    showToast({ variant: 'warning', title, message });
  }, [showToast]);

  const showInfo = useCallback((title: string, message?: string) => {
    showToast({ variant: 'info', title, message });
  }, [showToast]);

  const positionStyles = {
    'top-left': 'top-0 left-0 items-start',
    'top-right': 'top-0 right-0 items-end',
    'top-center': 'top-0 left-1/2 -translate-x-1/2 items-center',
    'bottom-left': 'bottom-0 left-0 items-start',
    'bottom-right': 'bottom-0 right-0 items-end',
    'bottom-center': 'bottom-0 left-1/2 -translate-x-1/2 items-center',
  };

  const value: ToastContextType = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    closeToast,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          className={clsx(
            'pointer-events-none fixed z-50 flex w-full max-w-sm flex-col gap-2 p-4',
            positionStyles[position]
          )}
          aria-live="polite"
          aria-atomic="true"
        >
          {toasts.map((toast) => (
            <Toast key={toast.id} {...toast} />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}


